// istanbul ignore file - barrel file

import type { Dispatch, SetStateAction } from "react";

import type { User } from "./src/User";

export { DeepReadonly } from "./src/DeepReadonly";
export { DeepPartial } from "./src/DeepPartial";
export { ExactlyOne } from "./src/ExactlyOne";
export { ExtractStrict } from "./src/ExtractStrict";
export { Manifest } from "./src/Manifest";
export { RequireKeys } from "./src/RequireKeys";
export { RequireOnlyOne } from "./src/RequireOnlyOne";
export { RequireAtLeastOne } from "./src/RequireAtLeastOne";
export { Thunk } from "./src/Thunk";

/**
 * Expands object types recursively
 * Credit: https://stackoverflow.com/questions/57683303/how-can-i-see-the-full-expanded-contract-of-a-typescript-type
 */
export type ExpandRecursively<T> = T extends object
  ? T extends infer O
    ? { [K in keyof O]: ExpandRecursively<O[K]> }
    : never
  : T;

export { KazooRequestHandler } from "./src/KazooRequestHandler";
export { KazooRequest } from "./src/KazooRequest";
export {
  ContextLike,
  ITracer,
  GraphQLParameters,
  PrincipalIdentifier,
} from "./src/ContextLike";
export { IKazooIdToken, AllowedRobot, ROBOTS } from "./src/IKazooIdToken";
export {
  User,
  UserCompanyInfo,
  UserRole,
  PeopleSegmentModule as IUserPeopleSegmentModule,
} from "./src/User";
export { TaskQueueLike } from "./src/TaskQueueLike";
export { IYeiV3Post, IYeiV3Args, IYeiV3Comment } from "./src/yeiV3";
export { I18nProps, TFunction } from "./src/i18n";

export {
  ErrorMetadata,
  KazooError,
  NotAuthenticatedError,
  NotFoundError,
  ForbiddenError,
  UserInputError,
  FieldValidationError,
  initializeError,
  IKazooErrorConstructable,
  getTranslationKeyAndValuesFromKazooErrorIfPossible,
  hasTranslationKey,
} from "./src/KazooError";

export { throwNotFoundIfAppropriate } from "./src/throwNotFoundIfAppropriate";
export {
  isDbOrGQLNotFoundError,
  PossibleNotFoundError,
} from "./src/isDbOrGQLNotFoundError";
export { userHasAnyIdMatching } from "./src/userHasAnyIdMatching";
export { sanitizeTokenForLogging } from "./src/sanitizeTokenForLogging";
export { PartialBy } from "./src/PartialBy";
export {
  KebabCase,
  SnakeCase,
  ScreamingSnakeCase,
  LowercaseKey,
  CamelCaseFromKebabCase,
  CamelCaseFromSnakeCase,
  KeyOfType,
} from "./src/keyOfTypes";

export {
  ImportBatchOption,
  GoalTaskInput,
  GoalMeasurableResultTaskInput,
  GroupLikeTaskInput,
  PersonTaskInput,
  GroupLikeEntityTypes,
  EntityIdentifierInput,
  NullableEntityIdentifierInput,
  PersonTaskCustomAttributeValuesInput,
  TaskMetadataInput,
} from "./src/integrations-directory";

export enum GraphQLErrorCode {
  BAD_USER_INPUT = "BAD_USER_INPUT",
  NOT_FOUND = "NOT_FOUND",
}

export interface IApplicationComponentProps {
  key: string;
  route: string;
  subdomain: string;
  features: { [key: string]: boolean };
  enableSubscriptions?: boolean;
  requestId: string;
}

export type IApplicationComponent = any;
export type SetStateFn<T> = Dispatch<SetStateAction<T>>;

export type LayoutOptions = "default" | "minimal" | "brandedMinimal";

export enum DateOptions {
  LAST_WEEK = "LAST_WEEK",
  LAST_30_DAYS = "LAST_30_DAYS",
  LAST_60_DAYS = "LAST_60_DAYS",
  LAST_90_DAYS = "LAST_90_DAYS",
  SINCE_LAUNCH = "SINCE_LAUNCH",
}

export interface IEncouragementArgs {
  role: string;
  date?: string;
}

export interface IEncouragement {
  iconUrl: string;
  program: string;
  reportUrl: string;
  label: string;
}

export interface IRewardsFulfillment {
  balance: number;
  isLowBalance: boolean;
  reportUrl: string;
  billingEmail: string;
}

export interface IApiVersion {
  sha: string;
  version: string;
}

export enum IPrivacy {
  RESTRICTED,
  PRIVATE,
  PUBLIC,
}
export enum CelebrationType {
  BIRTHDAY,
  ANNIVERSARY,
}

export interface ICoreValue {
  id?: string;
  name?: string;
  description?: string;
}

export interface ISkill {
  id?: string;
  name?: string;
  count?: number;
}

export interface IUserConnection {
  totalCount: number;
  nodes: User[];
}

export interface IComment {
  id?: string;
  from: User;
  message: string;
  hidden: boolean;
  createdAt?: string;
}

export interface ICommentConnection {
  totalCount: number;
  nodes: IComment[];
}

export interface IFlag {
  id: string;
  message?: string;
  userId: string;
}

export interface IRecognition {
  id: string;
  permalink: string;
  createdAt: string;
  updatedAt: string;
  privacy: IPrivacy;
  points: number;
  source: string;
  message: string;
  from: User;
  to: IUserConnection;
  highFives: IUserConnection;
  coreValue?: ICoreValue;
  skills: ISkill[];
  comments: ICommentConnection;
}

export enum ActivityFeedBackground {
  PRIMARY_COLOR = "PRIMARY_COLOR",
  GRAY = "GRAY",
}

export enum ActivityFeedRecipientNameColorType {
  DEFAULT = "DEFAULT",
  CUSTOM = "CUSTOM",
}

export interface IBranding {
  backgroundOverlayUrl?: string;
  headerTextAndIconColor: string;
  iconLogoUrl: string;
  loginLogoUrl: string;
  overlayBackgroundImage?: string;
  overlayCelebrationBackground?: boolean;
  overlayCelebrationSVG?: string;
  overlays?: string[];
  primaryColor: string;
  secondaryColor: string;
  siteBackgroundColor?: string;
  siteBackgroundUrl?: string;
  webLogoAltText: string;
  webLogoUrl: string;
  activityFeedBackground: ActivityFeedBackground;
  fileUploadErrorUrl?: string;
  activityFeedRecipientNameColorType: ActivityFeedRecipientNameColorType;
  activityFeedRecipientNameColor?: string;
  subdomain?: string;
}

export interface ICompanyLink {
  id: string;
  name: string;
  url: string;
}

export enum IProfileTabs {
  BEHAVIOR_BONUSES = "BEHAVIOR_BONUSES",
  CHECK_IN = "CHECK_IN",
  FEEDBACK = "FEEDBACK",
  NEW_FEEDBACK = "NEW_FEEDBACK",
  GOALS = "GOALS",
  NEW_CHECK_INS = "NEW_CHECK_INS",
  NEW_GOALS = "NEW_GOALS",
  LEGACY_RECOGNITIONS = "LEGACY_RECOGNITIONS",
  POINTS_HISTORY = "POINTS_HISTORY",
  RECOGNITIONS = "RECOGNITIONS",
  REWARDS = "REWARDS",
  SYNC_UP = "SYNC_UP",
  UNSET = "UNSET",
  INCENTIVES = "INCENTIVES",
  ACTIVITY_ATTRIBUTES_DASHBOARD = "ACTIVITY_ATTRIBUTES_DASHBOARD",
  RECOGNITION_APPROVALS = "RECOGNITION_APPROVALS",
}

export interface ProfileAccess {
  isSelf: boolean;
  bothYeiUsers: boolean;
  userIsFollowed: boolean;
  showActivityDownload: boolean;
}

export interface ITopActivities {
  coreValuesTotal: number;
  skillsTotal: number;
  coreValues: ICoreValue[];
  skills: ISkill[];
}

export enum IUserRole {
  EMPLOYEE,
  MANAGER,
  ADMIN,
}

export enum IManagerAction {
  PENDING_GOAL_APPROVAL,
  PENDING_CHECKIN,
}

export interface IActionItem {
  actionType?: string;
  avatar?: string;
  buttonText: string;
  buttonUrl: string;
  count?: number;
  dueDate?: Date;
  icon: string;
  role: string;
  title: string;
}

export enum GoalType {
  AD_HOC,
  COMPANY,
  DEPARTMENT,
  INDIVIDUAL,
}

export enum Engagement {
  PERFORMANCE = "PERFORMANCE",
  SOCIAL = "SOCIAL",
}

export interface IGoalLineItem {
  detailUrl: string;
  name: string;
  id: string;
  cycleId: string;
  cycleTitle: string;
  progress: number;
  goalStatus: string;
  participantType: string;
  isPublic: boolean;
  closeDueDate: string;
  lastUpdated: string;
  progressStatus: string;
  owner: string;
}

export enum IMaintenanceType {
  CATALOG_ITEM_NEW,
  YEI_SURVEY_NEW,
  NOMINATION_NEW,
  BEHAVIOR_BONUS_NEW,
  CHECKIN_CLOSURE,
  GOAL_CLOSURE,
}

export interface IMaintenanceItem {
  type: IMaintenanceType;
  role: IUserRole;
  message: string;
  actionText: string;
  actionUrl: string;
}

export interface ICelebrationsItem {
  id: string;
  name: string;
  type: string;
  avatar: string;
  date: string;
  yearsOfService?: number;
}

export interface IAnnouncementItem {
  id: string;
  message: string;
  publishDate: Date;
}

export enum NavigationSection {
  COMPANY = "COMPANY",
  HOME = "HOME",
  MODULES = "MODULES",
  PERFORMANCE_MANAGEMENT = "PERFORMANCE_MANAGEMENT",
  RECOGNITION_REWARDS = "RECOGNITION_REWARDS",
  /**
   * Legacy YEI Surveys
   */
  SURVEYS = "SURVEYS",
  /**
   * New Pulse / WorkTango Surveys
   */
  SURVEYS_AND_INSIGHTS = "SURVEYS_AND_INSIGHTS",
  TOOLS = "TOOLS",
}

export enum ParticipationType {
  BEHAVIOR_BONUS = "BEHAVIOR_BONUS",
  CHECK_IN = "CHECK_IN",
  GOALS = "GOALS",
  FEEDBACK = "FEEDBACK",
  RECOGNITION = "RECOGNITION",
  SYNC_UP = "SYNC_UP",
}

export interface NavSubItem {
  id: string;
  text: string;
  url: string;
  target?: string;
  embedUrl?: string;
  include?: boolean;
}

// YEI currently defines some sections. If this is the case we either want to just use yei and push
// sub items or, if YEI doesn't have any returned items for that section, use just the section and its sub
// items as defined in KW
export enum YeiSubSection {
  MODERATION = "Moderation",
  CATALOGS = "Catalogs",
}

export interface INavigationItem {
  id: string;
  icon?: string;
  section: NavigationSection;
  subItems: NavSubItem[];
  text: string | YeiSubSection;
  url: string;
  embedUrl?: string;
  include?: boolean;
}

export interface IMenuItem {
  icon?: string;
  id?: string;
  text: string;
  url?: string;
  weight?: number;
  subItems?: NavSubItem[];
}

export enum Overlay {
  SITE_BACKGROUND,
  CELEBRATION,
  RECOGNITION,
}

export enum BrandingHeaderColor {
  WHITE = "#FFFFFF",
  DARK_GREY = "#333333",
}

export interface ICelebrations {
  anniversaries: number;
  birthdays: number;
  usersBirthdays: IUserToolTip[];
  usersAnniversaries: IUserToolTip[];
  anniversariesUrl: string;
  birthdaysUrl: string;
}

export interface ITeamCelebration {
  name: string;
  type: string;
  avatar: string;
  date: string;
  yearsOfService: number | null;
}

export interface IDirectReport {
  fullName: string;
  title: string;
  avatar: string;
  profileUrl: string;
}

export interface IUserSearchResult {
  id: string;
  hgId?: string;
  hgUserId?: string;
  avatar?: string;
  image30: string;
  image70: string;
  initials?: string;
  name: string;
  jobTitle: string;
  department?: string;
  groupFeatureFlags?: Record<string, boolean>;
  group: string;
  profileUrl?: string;
  points?: {
    givingBalance?: number;
    redemptionBalance?: number;
    expirationDate?: string;
  };
}
export type IKWUserSearchResultManager = Omit<IKWUserSearchResult, "key">;
export interface IKWUserSearchResult {
  id: string;
  name: string;
  initials?: string;
  status?: "ACTIVE" | "ARCHIVED" | "PAUSED" | "PURGED";
  key: string;
  image70?: string;
  /*
   This is the property that the UserSelect component
   uses to render a helper text bellow the name of a user
  */
  jobTitle?: string | { name: string };
  /*
   This is the property that the UserSelect component
   uses to render a pill next to the name of a user
  */
  primaryGroup?: string | { name: string };
  location?: { id: string; name: string };
  /*
   This is the property that the UserSelect component
   uses to verify if the user is an admin.
  */
  adminIsSelected?: boolean;
  department?: { id: string };
  isAdmin?: boolean;
  legacyYeiIdentity?: {
    key: string;
  }[];
}

export interface FacepileUser {
  avatar?: string;
  department?: string;
  id: string;
  image160?: string;
  image30?: string;
  image70?: string;
  initials: string;
  jobTitle?: string;
  name: string;
  primaryGroup?: string;
  profileUrl?: string;
}

export interface IPostUser {
  fullName: string;
  id: string;
  image30?: string;
  image50?: string;
  image70?: string;
  initials?: string | undefined;
  profileUrl: string;
  title?: string;
}

export type IPostComment = {
  postId: string;
  from: IPostUser;
  hidden?: boolean;
  displayTime: string;
  id?: string;
  message: string;
};

export interface IContributingUsers {
  id: string;
}

export interface IRewardItem {
  canBeRedeemed?: boolean;
  description?: string;
  id: string;
  image?: string;
  imageLarge?: string;
  imageMedium?: string;
  imageSmall?: string;
  defaultProductImage?: string;
  name: string;
  points?: number;
  pooling?: boolean;
  poolingContributingUserIds?: IContributingUsers[];
  poolingDaysToGo?: number;
  poolingEndTime?: string;
  visible?: boolean;
}

export interface IUserToolTip {
  id: string;
  image30?: string;
  image70?: string;
  initials: string;
  name: string;
  jobTitle: string;
}

export interface INotificationRequest {
  detailUrl: string;
  items: INotification[];
}

export interface INotification {
  avatar: string;
  detailUrl: string;
  id: string;
  message: string;
  timestamp: Date;
}

export interface IEmployeeContributorItem {
  avatar: string;
  score: string;
  fullName: string;
  profileUrl: string;
}

export interface IGroupContributorItem {
  score: string;
  reportUrl: string;
  title: string;
}

export interface IEmployeeContributor {
  contributors: IEmployeeContributorItem[];
  label: string;
  url: string;
}

export interface IGroupContributor {
  contributors: IGroupContributorItem[];
  label: string;
  url: string;
}

export interface ITopContributors {
  dateOptions: string[];
  employeeContributor: IEmployeeContributor;
  groupContributor: IGroupContributor;
  tabs: Engagement[];
  title: string;
}

export interface IParticipationProgram {
  icon: string;
  percent: number;
  title: string;
  type: string;
  url: string;
}

export interface IParticipation {
  dateOptions: string[];
  title: string;
  programs: IParticipationProgram[];
}

export interface IPostArgs {
  groups?: string[]; // array of group ids to include
  olderThan?: string; // should be a post id, used for pagination
  newerThan?: string; // should be a p ost id, used for hasNewPosts
  rollupOlderThan?: string; // should be a UNIX timestamp, used for paginating when using rollup data
  perPage?: number;
  relationshipType?: string;
  types?: string[];
}

export enum PostRelationship {
  DIRECT_REPORTS,
  FULL_REPORTING_CHAIN,
  PEOPLE_I_FOLLOW,
}

export enum PostType {
  ADJUST_GIVING_POINTS,
  ADJUST_REDEMPTION_POINTS,
  AUTO_AWARD_ANNIVERSARY,
  AUTO_AWARD_BIRTHDAY,
  DONORS_CHOOSE_DONATION,
  GENERAL,
  RECOGNITION,
  SPECIAL_RECOGNITION,
  REDEEM_POINTS,
  SET_POINTS,
  INCENTIVES,
}

export interface IPost {
  comments?: IPostComment[];
  flags?: IFlag[];
  detailUrl: string;
  from: IPostUser;
  id: string;
  privacy: string;
  timestamp: string;
  type: string;
  selfReportingCommentTruncated?: string;
  permalink?: string | null;
}

export interface IFeedRecognition extends IPost {
  coreValue: ICoreValue;
  highFives?: IPostUser[];
  isBehaviorBonus?: boolean;
  message: string;
  permalink: string;
  points?: number | null; // should only be visible if I'm involved in the recognition
  tokenTypeName?: string;
  recipients: IPostUser[];
  skills: ISkill[];
  type: string;
}

export interface IRecognitionRecognition extends IFeedRecognition {
  to?: any;
  highFives: any;
  name?: string;
  createdAt?: string;
  comments: any;
  from: any;
  permalink: string;
  isPinned?: boolean;
  pinMessage?: string;
  pinDate?: string;
}

export interface IRewardRedemption extends IPost {
  createdAt: Date;
  detailUrl: string;
  from: IPostUser;
  id: string;
  isBehaviorBonus: boolean;
  item: IRewardItem;
  points?: number;
  privacy: string;
}

export interface IBehaviorBonusRedemption extends IRewardRedemption {
  highFives?: IPostUser[];
}

export type IRedemption = IRewardRedemption | IBehaviorBonusRedemption;

export interface IFeedItem {
  data: IRedemption[] | IFeedRecognition;
  id: string;
  isBehaviorBonus?: boolean;
  redemptionIds?: string[];
  rollupUrl?: string;
  timestamp?: string;
  type?: string;
}

export interface IPostsResult {
  posts: IPost[];
  rollups: {
    redemptionIds: string[];
  }[];
  rollupOlderThan: string;
}

export interface IActivityFeedResult {
  feed: IPostsResult;
  yeiApi_viewer: any;
}

export enum IYeiV3HttpMethods {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

export enum IButtonVariants {
  NONE = "",
  DISABLED = "disabled",
  PRIMARY = "primary",
  PRIMARY_INVERTED = "primary_inverted",
  SECONDARY = "secondary",
  SECONDARY_INVERTED = "secondary_inverted",
  SYSTEM = "system",
}

export enum BulkActionComponentEnums {
  ADD_POINTS = "ADD_POINTS",
  ASSIGN_DEPARTMENT = "ASSIGN_DEPARTMENT",
  ASSIGN_LOCATION = "ASSIGN_LOCATION",
  ASSIGN_GROUPS = "ASSIGN_GROUPS",
  SET_ACTIVE = "SET_ACTIVE",
  PAUSE_USERS = "PAUSE_USERS",
  ARCHIVE_USERS = "ARCHIVE_USERS",
  UPDATE_MANAGER = "UPDATE_MANAGER",
  ADJUST_POINTS = "ADJUST_POINTS",
  CHANGE_POINT_RULE = "CHANGE_POINT_RULE",
  PURGE = "PURGE",
}

export type BatchSourceIntegration = "SFTP" | "KAZOO";

// 1 char object properties used to keep file names uploaded to S3 short
// See rawToReadable in batchCreationQueue.ts
export interface RawCsvMetadata {
  u?: string; // uploader. If undefined, file was uploaded by super/platform admin
  i: BatchSourceIntegration; // integration
  n: string; // file name
  e?: boolean; // email
  c?: boolean; // create inactive
}

export interface CsvMetadata {
  // If undefined, CSV was uploaded by super/platform admin
  uploader?: string;
  integration: BatchSourceIntegration;
  name: string;
  emailOnCreation?: boolean;
  createInactive?: boolean;
}

export type ExtractTypeFromArray<T> = T extends (infer U)[] ? U : never;

export { IHasId, objectHasId } from "./src/IHasId";

export { UnwrapPromise } from "./src/UnwrapPromise";

export type ToastFunction = (
  content?: string | JSX.Element | undefined,
  timeout?: number | undefined
) => void;

export const DEFAULT_BRANDING_OPTIONS: Required<IBranding> = {
  activityFeedBackground: ActivityFeedBackground.PRIMARY_COLOR,
  backgroundOverlayUrl: "/layout/static/bg-overlay.png",
  fileUploadErrorUrl: "/layout/static/uploadfile_error.svg",
  headerTextAndIconColor: BrandingHeaderColor.DARK_GREY,
  iconLogoUrl: "/layout/static/default/icon_logo.png",
  loginLogoUrl: "/layout/static/default/login_logo.png",
  overlayBackgroundImage: "",
  overlayCelebrationBackground: false,
  overlayCelebrationSVG: "",
  overlays: ["CELEBRATIONS"],
  primaryColor: "#3094ED",
  secondaryColor: "#524FEF",
  siteBackgroundColor: "#EDEDED",
  siteBackgroundUrl: "",
  webLogoAltText: "WorkTango",
  webLogoUrl: "/layout/static/default/web_logo.png",
  activityFeedRecipientNameColorType:
    ActivityFeedRecipientNameColorType.DEFAULT,
  activityFeedRecipientNameColor: "",
  subdomain: "",
};

export const DEFAULT_TIMEZONE = "America/Chicago";

export type UserProfile = Omit<
  User,
  | "source"
  | "firstName"
  | "lastName"
  | "roles"
  | "secondaryManagerOf"
  | "departmentInfo"
  | "ids"
  | "department"
  | "email"
  | "title"
  | "birthday"
> & { ids?: User["ids"] } & {
  department?: User["department"];
} & { email?: User["email"] } & {
  title?: User["title"];
} & {
  birthday?: { month?: number; day?: number };
};
